import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useAppDispatch } from "src/store";
import {
  getEmailSetting,
  testEmailSetting,
  updateEmailSetting,
} from "src/store/emailSettings/saga";
import { EmailSettingDto } from "src/store/emailSettings/type";

const empty: EmailSettingDto = {
  SmtpHost: "",
  SmtpPort: 587,
  UserName: "",
  Password: "",
  EnableSsl: true,
  UseDefaultCredentials: false,
};

const EmailSettingsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<EmailSettingDto>(empty);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  document.title = t("EmailSettings.PageTitle");

  const set = (k: keyof EmailSettingDto, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const load = () => {
    dispatch(
      getEmailSetting({
        payload: {
          onSuccess: (_m: any, res: EmailSettingDto) => {
            if (res) setForm({ ...empty, ...res });
          },
          onError: () => {},
        },
      })
    );
  };
  useEffect(() => { load(); }, []);

  const onSave = () => {
    setSaving(true);
    dispatch(
      updateEmailSetting({
        payload: {
          onSuccess: (m: string) => {
            setSaving(false);
            toast.success(m || t("EmailSettings.Saved", "Kaydedildi").toString(), { theme: "colored" });
            load();
          },
          onError: (m: string) => {
            setSaving(false);
            toast.error(m, { theme: "colored" });
          },
        },
        body: {
          SmtpHost: form.SmtpHost,
          SmtpPort: Number(form.SmtpPort) || 0,
          UserName: form.UserName,
          Password: form.Password,
          EnableSsl: form.EnableSsl,
          UseDefaultCredentials: form.UseDefaultCredentials,
        },
      })
    );
  };

  const onTest = () => {
    if (!testEmail) {
      toast.error(t("EmailSettings.TestEmailRequired", "Test için bir e-posta girin").toString(), { theme: "colored" });
      return;
    }
    setTesting(true);
    dispatch(
      testEmailSetting({
        payload: {
          onSuccess: () => {
            setTesting(false);
            toast.success(t("EmailSettings.TestSent", "Test e-postası gönderildi").toString(), { theme: "colored" });
          },
          onError: (m: string) => {
            setTesting(false);
            // Backend SMTP hatasını mesajda döner (ör. '525 SMTP disabled').
            toast.error((t("EmailSettings.TestFailed", "Gönderilemedi") as string) + ": " + m, { theme: "colored", autoClose: 8000 });
          },
        },
        body: { email: testEmail },
      })
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xl={7} lg={9}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-1">{t("EmailSettings.PageTitle")}</h4>
                  <p className="text-muted mb-4">{t("EmailSettings.Desc")}</p>

                  <Row>
                    <Col md={8} className="mb-3">
                      <Label className="form-label">{t("EmailSettings.Host")}</Label>
                      <Input type="text" placeholder="smtp.yandex.com.tr" value={form.SmtpHost} onChange={(e) => set("SmtpHost", e.target.value)} />
                    </Col>
                    <Col md={4} className="mb-3">
                      <Label className="form-label">{t("EmailSettings.Port")}</Label>
                      <Input type="number" placeholder="587" value={form.SmtpPort} onChange={(e) => set("SmtpPort", e.target.value)} />
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <Label className="form-label">{t("EmailSettings.Username")}</Label>
                    <Input type="text" placeholder="support@rentificial.com" value={form.UserName} onChange={(e) => set("UserName", e.target.value)} />
                    <small className="text-muted">{t("EmailSettings.UsernameHint")}</small>
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">{t("EmailSettings.Password")}</Label>
                    <Input type="password" autoComplete="new-password" placeholder="••••••••" value={form.Password} onChange={(e) => set("Password", e.target.value)} />
                    <small className="text-muted">{t("EmailSettings.PasswordHint")}</small>
                  </div>

                  <div className="form-check form-switch mb-2">
                    <Input type="checkbox" className="form-check-input" id="enableSsl" checked={form.EnableSsl} onChange={(e) => set("EnableSsl", e.target.checked)} />
                    <Label className="form-check-label" htmlFor="enableSsl">{t("EmailSettings.EnableSsl")}</Label>
                  </div>
                  <div className="form-check form-switch mb-4">
                    <Input type="checkbox" className="form-check-input" id="useDefaultCreds" checked={form.UseDefaultCredentials} onChange={(e) => set("UseDefaultCredentials", e.target.checked)} />
                    <Label className="form-check-label" htmlFor="useDefaultCreds">{t("EmailSettings.UseDefaultCreds")}</Label>
                  </div>

                  <Button color="success" onClick={onSave} disabled={saving}>
                    <i className="bx bx-save me-1" /> {t("EmailSettings.Save")}
                  </Button>

                  <hr className="my-4" />

                  <h5 className="mb-1">{t("EmailSettings.TestTitle")}</h5>
                  <p className="text-muted mb-3">{t("EmailSettings.TestDesc")}</p>
                  <Row className="g-2 align-items-end">
                    <Col md={8}>
                      <Label className="form-label">{t("EmailSettings.TestEmail")}</Label>
                      <Input type="email" placeholder="ornek@mail.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Button color="primary" className="w-100" onClick={onTest} disabled={testing}>
                        <i className="bx bx-send me-1" /> {t("EmailSettings.SendTest")}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EmailSettingsPage;
